<template>
  <div class="col-12 col-xl-12">
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ t("gradeSheets.title") }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loading"
            :message="t('gradeSheets.loading')"
            class="py-5"
          />

          <template v-else>
            <!-- Mobile view -->
            <div class="d-block d-xl-none">
              <div v-if="gradeSheets.length === 0" class="text-center py-3 text-muted">
                <template v-if="searchQuery">
                  <i class="fas fa-search fa-2x mb-2"></i>
                  <p>{{ t("gradeSheets.noSearchResults") }}</p>
                </template>
                <template v-else>
                  <i class="fas fa-folder-open fa-2x mb-2"></i>
                  <p>{{ t("gradeSheets.noData") }}</p>
                </template>
              </div>

              <div
                v-for="gradeSheet in gradeSheets"
                :key="'mobile-'+gradeSheet.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 class="mb-0 text-sm">{{ gradeSheet.title }}</h6>
                      <p class="text-xs text-muted mb-0">
                        {{ gradeSheet.class?.name || '—' }} - {{ gradeSheet.subject?.name || '—' }}
                      </p>
                      <p class="text-xs text-muted mb-0" v-if="gradeSheet.academic_year">
                        {{ gradeSheet.academic_year.name }} ({{ gradeSheet.academic_year.year }})
                        <span v-if="gradeSheet.academic_year.is_active" class="badge bg-success ms-1">{{ t('common.current') }}</span>
                      </p>
                    </div>
                    <div class="d-flex">
                      <button
                        @click="navigateToGradeSheetView(gradeSheet.id)"
                        class="btn btn-link text-success p-0 mx-1"
                        :title="t('gradeSheets.viewGradeSheet')"
                        v-can="'grade_sheets.display'"
                      >
                        <i class="fas fa-file-alt fa-sm"></i>
                      </button>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <button
                      @click="navigateToEdit(gradeSheet.id)"
                      class="btn btn-link text-secondary p-0 mx-1"
                      :title="t('actions.edit')"
                      v-can="'grade_sheets.update'"
                    >
                      <i class="fas fa-edit fa-xs"></i>
                    </button>
                    <button
                      @click="$emit('delete', gradeSheet)"
                      class="btn btn-link text-danger p-0 mx-1"
                      :title="t('actions.delete')"
                      v-can="'grade_sheets.delete'"
                    >
                      <i class="fas fa-trash fa-xs"></i>
                    </button>
                    
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop view -->
            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t("gradeSheets.columns.title") }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t("gradeSheets.columns.class") }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t("gradeSheets.columns.subject") }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t("gradeSheets.columns.academic_year") }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 pe-4 text-center">{{ t("gradeSheets.columns.actions") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="gradeSheets.length === 0">
                  <td colspan="5" class="text-center py-4 text-muted">
                    <template v-if="searchQuery">
                      <i class="fas fa-search fa-2x mb-2"></i>
                      <p>{{ t("gradeSheets.noSearchResultsFor", { query: searchQuery }) }}</p>
                    </template>
                    <template v-else>
                      <i class="fas fa-folder-open fa-2x mb-2"></i>
                      <p>{{ t("gradeSheets.noData") }}</p>
                    </template>
                  </td>
                </tr>

                <tr v-for="gradeSheet in gradeSheets" :key="gradeSheet.id">
                  <td>
                    <div class="d-flex px-3 py-1">
                      <div class="d-flex flex-column justify-content-center">
                        <h6 class="mb-0 text-md">{{ gradeSheet.title }}</h6>
                      </div>
                    </div>
                  </td>
                  <td>
                    <p class="text-xs font-weight-bold mb-0">{{ gradeSheet.class?.name || 'N/A' }}</p>
                  </td>
                  <td>
                    <p class="text-xs font-weight-bold mb-0">{{ gradeSheet.subject?.name || 'N/A' }}</p>
                  </td>
                  <td>
                    <p class="text-xs font-weight-bold mb-0">
                      {{ gradeSheet.academic_year?.name || 'N/A' }}
                      <span v-if="gradeSheet.academic_year?.is_active" class="badge bg-success ms-1">{{ t('common.current') }}</span>
                    </p>
                  </td>
                  <td class="align-middle text-center">
                    <div class="d-inline-flex">
                      <button
                        @click="navigateToGradeSheetView(gradeSheet.id)"
                        class="btn btn-link text-success p-1 mx-1"
                        :title="t('gradeSheets.viewGradeSheet')"
                        v-can="'grade_sheets.display'"
                      >
                        <i class="fas fa-file-alt fa-sm"></i>
                      </button>
                      
                      <button
                        @click="navigateToEdit(gradeSheet.id)"
                        class="btn btn-link text-secondary p-1 mx-1"
                        :title="t('actions.edit')"
                        v-can="'grade_sheets.update'"
                      >
                        <i class="fas fa-edit fa-sm"></i>
                      </button>
                      <button
                        @click="$emit('delete', gradeSheet)"
                        class="btn btn-link text-danger p-1 mx-1"
                        :title="t('actions.delete')"
                        v-can="'grade_sheets.delete'"
                      >
                        <i class="fas fa-trash fa-sm"></i>
                      </button>
               
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t } = useI18n();
const router = useRouter();

 defineProps({
  gradeSheets: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const searchQuery = ref('');

const navigateToGradeSheetView = (id) => {
  router.push({ name: 'GradeSheetView', params: { id } });
};

const navigateToEdit = (id) => {
  router.push(`/grade-sheets/${id}/edit`);
};
</script>